import { features } from '@/config/features';
import { useIdentity, type Identity } from './identity';

/**
 * May this user play a level right now? The only gating surface in the app is the level cards
 * and the /play route, both of which call `useAccess`. With billing off, everyone is allowed.
 */
export type Access = { allowed: true } | { allowed: false; reason: 'sign-in' | 'expired' | 'no-tokens' };

/** One row per grant; codes, Stripe and tokens all produce this shape (see docs/PLAN.md §13). */
export interface Entitlement {
  kind: 'subscription' | 'code' | 'tokens' | 'trial' | 'manual';
  validFrom: string;
  validUntil: string | null;
  tokensLeft: number | null;
}

export function isActive(e: Entitlement, now: Date = new Date()): boolean {
  const t = now.getTime();
  if (t < Date.parse(e.validFrom)) return false;
  if (e.validUntil !== null && t > Date.parse(e.validUntil)) return false;
  if (e.tokensLeft !== null && e.tokensLeft <= 0) return false;
  return true;
}

/** Pure policy, unit-tested; the data source is whatever adapter fills `entitlements`. */
export function decide(identity: Identity, entitlements: readonly Entitlement[], now: Date = new Date()): Access {
  if (!features.billing) return { allowed: true };
  if (identity.status !== 'signed-in') return { allowed: false, reason: 'sign-in' };
  if (entitlements.some((e) => isActive(e, now))) return { allowed: true };
  const hadTokens = entitlements.some((e) => e.kind === 'tokens');
  return { allowed: false, reason: hadTokens ? 'no-tokens' : 'expired' };
}

/** Local implementation: no entitlements exist yet, so the decision depends on the billing flag only. */
/** `_taskId` is part of the port contract for later per-level entitlements. */
export function useAccess(_taskId: string): Access {
  const identity = useIdentity((s) => s.identity);
  return decide(identity, []);
}
